import { zonedDateParts, zonedMidnightToUtc } from "../zoned-dates";
import type { ClientAccessRestriction } from "./client-access";
import type { LastUpdatedFilter } from "./compliance-review";
import type { IdentityVerificationSummary } from "./identity-verification";
import type { PageQuery, PaginatedResponse } from "./pagination";

/**
 * QL-136 — the advisor clients list. Search, view filters, sorting and paging
 * all run on the server; the caller only names its choices.
 */

export type ClientListSortDir = "asc" | "desc";

export type ClientListQuery = PageQuery & {
  /** Case-insensitive substring over name, email, SSN, org id and phone. */
  search?: string;
  /** A client view id (standard `standard-*` or a custom view) whose filters apply. */
  viewId?: string;
  /** Restrict to one client type (Person / Company / Group). */
  type?: string;
  /**
   * Column id to order by: a `standard-*` field id or a custom field id.
   * The server rejects ids it does not know. Defaults to `standard-name`.
   */
  sortBy?: string;
  sortDir?: ClientListSortDir;
  /**
   * QL-349 — IANA zone from the browser (`Intl.DateTimeFormat().resolvedOptions().timeZone`),
   * anchoring any `datePeriod` filter's calendar-to-date boundary. Missing or
   * invalid falls back to `Europe/Oslo`.
   */
  timezone?: string;
  /**
   * QL-349 — ad-hoc filters from the `/clients` filter bar, ANDed with the
   * selected view's own filters (both go through the same
   * `buildFilterConditions`, so combining them is just concatenation).
   * Cannot be saved — only a designer-built view persists.
   */
  filters?: ClientViewFilter[];
};

// ─── Client view filters (QL-349) ──────────────────────────────────────────
//
// The ONE definition of a client filter, evaluated in SQL by
// `buildFilterConditions` and consumed by every caller that resolves a saved
// view or an ad-hoc filter: the `/clients` list, compliance clients review,
// batch AML lookup, and chat/MCP `client_read`. Do not redeclare this
// type anywhere else — import it from here.

/** The six client timestamps a rolling-window or calendar-period date filter can target. */
export const ACTIVITY_DATE_FIELDS = [
  "lastLogin",
  "lastInfoUpdated",
  "lastFlowActivity",
  "lastFlowCompleted",
  "lastAccountSynced",
  "lastSignedDocument",
] as const;
export type ActivityDateField = (typeof ACTIVITY_DATE_FIELDS)[number];

/**
 * Calendar-to-date periods for a `datePeriod` filter — exactly `LastUpdatedFilter`
 * (from `./compliance-review`, reused rather than redefined per the ticket) minus
 * `"anytime"`: no filter row is ever anytime, since removing the filter is anytime.
 * `satisfies` ties this list to `LastUpdatedFilter` without a second source of truth —
 * it would fail to compile if the two ever diverged.
 */
export const WITHIN_DATE_PERIODS = [
  "today",
  "this_week",
  "this_month",
  "this_quarter",
  "this_year",
] as const satisfies readonly Exclude<LastUpdatedFilter, "anytime">[];

/**
 * QL-512 — "more than … ago" periods for a `datePeriod` filter: the date is
 * before a rolling cutoff counted back from today (see `olderThanCutoff`). A
 * client with no date at all — never logged in, never contacted — matches
 * every one of them, the same as the legacy `activityDate` `not_in_last`.
 */
export const OLDER_THAN_DATE_PERIODS = [
  "older_than_month",
  "older_than_quarter",
  "older_than_half_year",
  "older_than_year",
] as const;
export type OlderThanDatePeriod = (typeof OLDER_THAN_DATE_PERIODS)[number];

export const DATE_PERIODS = [...WITHIN_DATE_PERIODS, ...OLDER_THAN_DATE_PERIODS] as const;
export type DatePeriod = (typeof DATE_PERIODS)[number];

export function isOlderThanDatePeriod(period: DatePeriod): period is OlderThanDatePeriod {
  return (OLDER_THAN_DATE_PERIODS as readonly string[]).includes(period);
}

const OLDER_THAN_MONTHS = {
  older_than_month: 1,
  older_than_quarter: 3,
  older_than_half_year: 6,
  older_than_year: 12,
} as const satisfies Record<OlderThanDatePeriod, number>;

/**
 * The cutoff of an "older than" period: midnight, in `timezone`, of the
 * calendar date N months before today there. A date strictly before it is
 * "more than N months ago" — so on 1 October, "more than a month ago" means
 * 31 August or earlier. A day the target month lacks (31 March → February)
 * is clamped to that month's last day rather than rolling into the next.
 */
export function olderThanCutoff(period: OlderThanDatePeriod, now: Date, timezone: string): Date {
  const { year, month, day } = zonedDateParts(now, timezone);
  const targetMonth = month - OLDER_THAN_MONTHS[period];
  const daysInTargetMonth = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
  return zonedMidnightToUtc(year, targetMonth, Math.min(day, daysInTargetMonth), timezone);
}

export const NUMERIC_FILTER_OPERATORS = ["gt", "gte", "lt", "lte"] as const;
export type NumericFilterOperator = (typeof NUMERIC_FILTER_OPERATORS)[number];

/** Standard client fields that only support an empty / not-empty filter. */
export const EMPTY_FILTERABLE_STANDARD_FIELDS = [
  "email",
  "mobilePhone",
  "orgId",
  "externalId",
] as const;
export type EmptyFilterableStandardField = (typeof EMPTY_FILTERABLE_STANDARD_FIELDS)[number];

/** Names of the standard client views — the one list the view service and the AI tool both read. */
export const STANDARD_CLIENT_VIEW_NAMES = {
  allClients: "All Clients",
  myClients: "My Clients",
  myFavourites: "My Favourites",
} as const;
export type StandardClientViewName =
  (typeof STANDARD_CLIENT_VIEW_NAMES)[keyof typeof STANDARD_CLIENT_VIEW_NAMES];

export type ClientViewFilter =
  | { type: "country"; values: string[] }
  | { type: "language"; values: string[] }
  | { type: "costCategory"; values: string[] }
  /** "__ME__" resolves to the calling user. */
  | { type: "owner"; values: string[] }
  | { type: "clientType"; values: string[] } // Person | Company | Group
  /** QL-102 — clients the given user has starred. "__ME__" resolves to the calling user. */
  | { type: "favourite"; values: string[] }
  /**
   * QL-380 — clients linked to any of these teams (team ids). A pure label
   * filter: it applies whatever the access settings, and never widens what the
   * caller may see (visibility is a separate predicate).
   */
  | { type: "team"; values: string[] }
  /** Completed years as of today, from date of birth. Person only; no date of birth matches nothing. */
  | { type: "age"; operator: NumericFilterOperator; years: number }
  /** Sum of synced-account cash, converted to the tenant currency. An unconvertible account matches nothing. */
  | { type: "cash"; operator: NumericFilterOperator; amount: number }
  | {
      type: "standardEmpty";
      field: EmptyFilterableStandardField;
      operator: "empty" | "notEmpty";
    }
  /**
   * LEGACY rolling window ("in the last N months" / "not in the last N
   * months"). Kept forever, byte-for-byte, so views saved before QL-349 keep
   * resolving to the same clients — new views use `datePeriod` instead.
   */
  | {
      type: "activityDate";
      dateField: ActivityDateField;
      operator: "in_last" | "not_in_last";
      months: number;
    }
  /** Calendar-to-date (QL-349) or "more than … ago" (QL-512) filter. `lastClientCommunication` is a computed pseudo-field, not a stored column. */
  | {
      type: "datePeriod";
      dateField: ActivityDateField | "lastClientCommunication";
      period: DatePeriod;
    }
  | ({ type: "customField"; fieldId: string } & (
      | { operator: "empty" | "notEmpty" } // text, textarea
      | { operator: NumericFilterOperator; value: number } // number
      | { operator: "isAnyOf"; values: string[] } // dropdown, radio
      | { operator: "containsAnyOf"; values: string[] } // multiselect
      | { operator: "boolean"; value: boolean } // boolean
    ));

export type ClientListCustomValue = {
  customFieldId: string;
  value: string;
};

export type ClientListItem = {
  id: string;
  name: string;
  type: string | null;
  email: string | null;
  mobilePhone: string | null;
  ssn: string | null;
  dateOfBirth: string | null;
  orgId: string | null;
  country: string | null;
  language: string | null;
  clientCostCategoryId: string | null;
  ownerId: string | null;
  /** `deletedAt` is set when the owner was deleted (QL-365); the UI marks the name. */
  owner: { id: string; name: string | null; deletedAt: string | null } | null;
  customValues: ClientListCustomValue[];
  identity: IdentityVerificationSummary | null;
  /** QL-380 — the stored Private flag (only evaluated in Private mode). */
  isPrivate: boolean;
  /** QL-380 — the teams the client is limited to, by name; names them in the list's team icon. */
  teams: { id: string; name: string }[];
  /** QL-380 — the restriction under the tenant's current mode; drives the lock icon. */
  accessRestriction: ClientAccessRestriction;
  lastLogin: string | null;
  lastInfoUpdated: string | null;
  lastFlowActivity: string | null;
  lastFlowCompleted: string | null;
  lastAccountSynced: string | null;
  lastSignedDocument: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ClientListResponse = PaginatedResponse<ClientListItem>;
